<!--
  TagPicker — G1.7 widget parity for the admin TagPickerComponent.

  Popover that lists every catalog tag with checkboxes; toggling fires
  apply/remove immediately. Inline "Create new tag" footer that POSTs to
  /api/tags and auto-applies the new tag to the active conversation.

  Wired from ConversationHeader: the Tags trigger button anchors this
  component, host owns open/closed state. Self-closes on outside click
  or Escape. Emits `manage` when the operator clicks "Manage tags..." in
  the footer so the host can mount TagManageModal.
-->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { tagsList, type TagRef } from '../lib/api/queries.js';
  import { applyTagMutation, removeTagMutation, createTagMutation } from '../lib/api/mutations.js';

  type Props = {
    conversationId: string;
    appliedTags: TagRef[];
    onClose: () => void;
    onManage: () => void;
    /**
     * Trigger element to anchor the picker against. With `position: fixed`
     * (host style below) the picker doesn't follow any DOM ancestor —
     * it floats over scrollable parents without being clipped. Computed
     * top/right from the anchor's getBoundingClientRect on mount.
     */
    anchor?: HTMLElement | null;
  };
  let { conversationId, appliedTags, onClose, onManage, anchor = null }: Props = $props();

  const queryClient = useQueryClient();
  const catalogQuery = $derived(createQuery(tagsList()));

  // Mutations re-derive when conversationId changes (operator switches
  // threads while picker is open — unlikely but cheap to handle).
  const apply = $derived(createMutation(applyTagMutation(queryClient, conversationId)));
  const remove = $derived(createMutation(removeTagMutation(queryClient, conversationId)));
  const create = $derived(createMutation(createTagMutation(queryClient)));

  let newName = $state('');
  let newColor = $state('#f0a020');
  let errorMessage = $state<string | null>(null);
  let busyTagId = $state<string | null>(null);

  const appliedIds = $derived(new Set(appliedTags.map((t) => t.id)));

  async function toggle(tagId: string, checked: boolean) {
    busyTagId = tagId;
    errorMessage = null;
    try {
      if (checked) {
        await $apply.mutateAsync({ tagId });
      } else {
        await $remove.mutateAsync({ tagId });
      }
    } catch (err) {
      errorMessage = err instanceof Error ? err.message : 'Unknown error.';
    } finally {
      busyTagId = null;
    }
  }

  async function submitCreate() {
    const name = newName.trim();
    if (name.length === 0 || $create.isPending) return;
    errorMessage = null;
    try {
      const created = await $create.mutateAsync({ name, color: newColor });
      // Auto-apply the new tag to the active conversation — operators
      // almost always want this. Mirrors admin's picker UX exactly.
      await $apply.mutateAsync({ tagId: created.id });
      newName = '';
    } catch (err) {
      errorMessage = err instanceof Error ? err.message : 'Unknown error.';
    }
  }

  // ---- outside-click + Escape -----------------------------------------

  let pickerEl: HTMLDivElement | null = $state(null);

  function handleDocClick(event: MouseEvent) {
    const target = event.target as Node | null;
    if (!target) return;
    if (pickerEl?.contains(target)) return;
    onClose();
  }
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') onClose();
  }
  /**
   * Pin the picker's top-right corner relative to the anchor's bottom-right.
   * 6px gap; flips upward if it would overflow the viewport bottom.
   */
  function positionTo(el: HTMLElement) {
    if (!pickerEl) return;
    const rect = el.getBoundingClientRect();
    const pickerWidth = 280;
    const pickerEstHeight = 380;
    const gap = 6;
    const right = Math.max(8, window.innerWidth - rect.right);
    let top = rect.bottom + gap;
    if (top + pickerEstHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - gap - pickerEstHeight);
    }
    pickerEl.style.top = `${top}px`;
    pickerEl.style.right = `${right}px`;
    pickerEl.style.width = `${pickerWidth}px`;
  }

  function handleResize() {
    if (anchor) positionTo(anchor);
  }

  onMount(() => {
    document.addEventListener('click', handleDocClick);
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('resize', handleResize);
    if (anchor) positionTo(anchor);
  });
  onDestroy(() => {
    document.removeEventListener('click', handleDocClick);
    document.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('resize', handleResize);
  });
</script>

<div class="picker" role="dialog" aria-label="Edit tags" bind:this={pickerEl}>
  <header class="picker-header">
    <strong>Tags</strong>
    <button type="button" class="close-btn" aria-label="Close tag picker" onclick={onClose}>×</button>
  </header>

  {#if $catalogQuery.isPending}
    <div class="empty" role="status">Loading...</div>
  {:else if $catalogQuery.error}
    <div class="empty error">Could not load tags.</div>
  {:else if !$catalogQuery.data || $catalogQuery.data.length === 0}
    <div class="empty">
      <p>No tags yet.</p>
      <p class="hint">Create one below.</p>
    </div>
  {:else}
    <ul class="catalog" role="list">
      {#each $catalogQuery.data as t (t.id)}
        <li>
          <label class="tag-row">
            <input
              type="checkbox"
              checked={appliedIds.has(t.id)}
              disabled={busyTagId === t.id}
              onchange={(e) => toggle(t.id, (e.currentTarget as HTMLInputElement).checked)}
            />
            <span class="swatch" style="background-color: {t.color}" aria-hidden="true"></span>
            <span class="name">{t.name}</span>
          </label>
        </li>
      {/each}
    </ul>
  {/if}

  <form
    class="create-row"
    onsubmit={(e) => {
      e.preventDefault();
      void submitCreate();
    }}
  >
    <input
      type="text"
      class="name-input"
      bind:value={newName}
      placeholder="New tag name"
      maxlength="64"
      aria-label="New tag name"
      disabled={$create.isPending}
    />
    <input
      type="color"
      class="color-input"
      bind:value={newColor}
      aria-label="New tag color"
      disabled={$create.isPending}
    />
    <button
      type="submit"
      class="create-btn"
      disabled={$create.isPending || newName.trim().length === 0}
    >
      {$create.isPending ? '...' : 'Add'}
    </button>
  </form>

  {#if errorMessage}
    <div class="error-bar" role="alert">{errorMessage}</div>
  {/if}

  <footer class="picker-footer">
    <button type="button" class="manage-link" onclick={onManage}>Manage tags...</button>
  </footer>
</div>

<style>
  /* Right-panel reshuffle — picker hosts at viewport-fixed coords
     computed from the trigger button's getBoundingClientRect via the
     positionTo() helper above. Frees the picker from the panel's
     overflow-y: auto clipping. Initial top/right set so an early
     mount doesn't flash at 0,0 before positionTo() runs. */
  .picker {
    position: fixed;
    top: 0;
    right: 0;
    width: 280px;
    z-index: 50;
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: var(--widget-radius, 8px);
    box-shadow:
      0 1px 2px oklch(20% 0.01 250 / 0.06),
      0 10px 28px oklch(20% 0.01 250 / 0.14);
    font-family: var(--widget-font);
    font-size: 12.5px;
    color: var(--widget-fg, oklch(20% 0.01 250));
    overflow: hidden;
  }

  .picker-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    border-bottom: 1px solid var(--widget-divider, oklch(93% 0.006 250));
  }
  .picker-header strong {
    font-size: 12.5px;
    font-weight: 600;
  }
  .close-btn {
    background: none;
    border: none;
    font-size: 18px;
    line-height: 1;
    padding: 0 4px;
    cursor: pointer;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .close-btn:hover {
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .close-btn:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
    border-radius: 4px;
  }

  .empty {
    padding: 16px 12px;
    text-align: center;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .empty.error {
    color: var(--widget-danger, oklch(55% 0.2 25));
  }
  .empty .hint {
    margin: 4px 0 0;
    font-size: 11.5px;
    color: var(--widget-fg-dim, oklch(64% 0.01 250));
  }

  .catalog {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    max-height: 240px;
    overflow-y: auto;
  }
  .tag-row {
    display: grid;
    grid-template-columns: auto auto 1fr;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    cursor: pointer;
  }
  .tag-row:hover {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 4%, transparent);
  }
  .tag-row input[type='checkbox'] {
    accent-color: var(--widget-accent, oklch(75% 0.165 75));
    cursor: pointer;
  }
  .tag-row input[type='checkbox']:disabled {
    cursor: default;
    opacity: 0.5;
  }
  .swatch {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    border: 1px solid color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 15%, transparent);
  }
  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .create-row {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 6px;
    padding: 8px 10px;
    border-top: 1px solid var(--widget-divider, oklch(93% 0.006 250));
    background: var(--widget-bg-sunk, oklch(96% 0.006 250));
  }
  .name-input {
    padding: 5px 8px;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: 5px;
    font-family: var(--widget-font);
    font-size: 12.5px;
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .name-input:focus {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: -1px;
    border-color: var(--widget-accent, oklch(75% 0.165 75));
  }
  .color-input {
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: 5px;
    cursor: pointer;
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
  }
  .create-btn {
    padding: 0 10px;
    border-radius: 5px;
    background: var(--widget-accent, oklch(75% 0.165 75));
    color: var(--widget-fg, oklch(20% 0.01 250));
    font-weight: 600;
    font-size: 12px;
    border: none;
    cursor: pointer;
  }
  .create-btn:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  .create-btn:focus-visible {
    outline: 2px solid var(--widget-accent-deep, oklch(50% 0.19 65));
    outline-offset: 2px;
  }
  .create-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .error-bar {
    padding: 8px 10px;
    background: var(--widget-danger-tint, oklch(94% 0.04 25));
    color: var(--widget-danger, oklch(55% 0.2 25));
    font-size: 11.5px;
    border-top: 1px solid var(--widget-danger, oklch(55% 0.2 25));
  }

  .picker-footer {
    padding: 8px 10px;
    border-top: 1px solid var(--widget-divider, oklch(93% 0.006 250));
    background: var(--widget-bg-sunk, oklch(96% 0.006 250));
  }
  .manage-link {
    background: none;
    border: none;
    padding: 0;
    font-size: 11.5px;
    font-weight: 500;
    color: var(--widget-accent-deep, oklch(50% 0.19 65));
    cursor: pointer;
    text-decoration: underline;
    font-family: inherit;
  }
  .manage-link:hover {
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .manage-link:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
    border-radius: 2px;
  }
</style>
